import { useTranslation } from 'react-i18next';

import type { ActivityBand } from '@/api/types';

import { Badge } from './ui';

// Row 93 names three colours. The fourth band is deliberately grey rather than
// red: an account that has not been seen since tracking started has not gone
// quiet on us, and colouring it red would report churn that never happened.
const TONES = {
  active: 'positive',
  lapsing: 'caution',
  dormant: 'critical',
  unknown: 'neutral',
} as const;

export function ActivityBadge({ band }: { band: ActivityBand }) {
  const { t } = useTranslation();
  return (
    <Badge tone={TONES[band]} dot title={t(`activity.${band}Hint`)}>
      {t(`activity.${band}`)}
    </Badge>
  );
}

/**
 * What the four colours mean, spelled out next to the table.
 *
 * The bands are thresholds in days, and nothing on the row itself says so.
 * Without this the reader has to guess whether amber is a fortnight or a
 * quarter.
 */
export function ActivityLegend() {
  const { t } = useTranslation();
  const bands: ActivityBand[] = ['active', 'lapsing', 'dormant', 'unknown'];
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-2xs text-ink-subtle">
      <span className="font-medium">{t('activity.legend')}</span>
      {bands.map((band) => (
        <span key={band} className="inline-flex items-center gap-1.5">
          <ActivityBadge band={band} />
          <span>{t(`activity.${band}Hint`)}</span>
        </span>
      ))}
    </div>
  );
}
